import { phaseOf, type CountdownPhase, type PhaseInput } from '@/lib/task-phase';
import { isActiveAcceptedStatus } from '@/lib/task-status';
import type { CustomerRequestTab } from '@/features/tasks/tab-params';
import type { OfferRow, TaskRow } from '@/features/tasks/types';

/**
 * MUSTERININ TALEP DETAYI — SAF KURALLAR (2026-10-02, M2/M6).
 *
 * Bilesenden (RequestOffersPage) ayri: secimin acildigi saniye, varsayilan siralama ve kazanan
 * sabitleme ancak enjekte edilen bir saatle / duz verilerle sinanabilir
 * (__tests__/request-detail.test.ts).
 *
 * ============================ YENI SOZLESME ================================
 * Talep sahibi (ve musteri org yoneticisi) teklifleri TEKLIF FAZINDA da gorur: teklifler
 * geldikce listeye duser, buro teklif suresi icinde fiyatini guncelleyebilir. SECIM yine yalniz
 * KARAR fazinda: sunucu onceki onayi 409 OFFER_WINDOW_OPEN ile reddeder (task-status.js PENDING
 * window:'decide'). 2026-09-23 - 2026-10-02 arasi (karar Q2) sahibi teklif fazinda hicbir teklif
 * gormuyordu; o kural KALKTI.
 */

/** Talep canliyken (teklif/karar fazinda 'new') teklif listesi ve talep satiri bu aralikla yoklanir. */
export const LIVE_POLL_MS = 5_000;

/**
 * SAAT SAPMASI PAYI. Secim, istemci sayaci karar fazina GECTIKTEN bu kadar sonra acilir.
 *
 * Istemci saati sunucuya `Date` basligiyla hizalaniyor (lib/clock-skew.ts) ama baslik SANIYE
 * cozunurlugunde ve gidis-donus suresini icermiyor: hizalanmis saat bir-iki saniye ONDE olabilir.
 * Sayac 0:00'a vurdugu an basilan "Sec" sunucuda hala teklif fazina duser ve 409 alir. Pay bunu
 * kapatir; yine de 409 gelirse ekran genel hata yerine "birkac saniye" der (SELECTION_ERROR_KEYS).
 */
export const SELECTION_GRACE_MS = 2_000;

export type SelectionGate =
  /** Teklif fazi (ya da karar fazinin ilk SELECTION_GRACE_MS'i): satirlar gorunur, secim KAPALI. */
  | { state: 'waiting'; opensInMs: number }
  /** Karar fazi: secim ACIK. */
  | { state: 'open' }
  /** Karar suresi doldu ya da talep zaten acik degil ('pending', 'cancelled' ...). */
  | { state: 'closed' };

const OPEN: SelectionGate = { state: 'open' };
const CLOSED: SelectionGate = { state: 'closed' };

/**
 * Secim su an acik mi; degilse ne zaman acilir?
 *
 * Hesap, fazin `nowMs - SELECTION_GRACE_MS` anindaki haline bakar: o an hala 'offer' ise secim
 * KAPALI ve kalan sure tam olarak secimin acilmasina kalan suredir (sayac + pay). Faz hesabi
 * lib/task-phase.ts'teki AYNI fonksiyon (phaseOf) — ikinci bir faz makinesi kurulmuyor. Sunucu
 * satirda zaten 'decide' diyorsa (geri sayim yolu) pay gerekmez: sunucu karar fazini KENDI
 * saatiyle onaylamis demektir, phaseOf da gecmis bir an icin 'decide' doner.
 */
export const selectionGate = (input: PhaseInput, nowMs: number): SelectionGate => {
  const current = phaseOf(input, nowMs);
  if (current.phase !== 'offer' && current.phase !== 'decide') return CLOSED;
  const lagged = phaseOf(input, nowMs - SELECTION_GRACE_MS);
  if (lagged.phase === 'offer') return { state: 'waiting', opensInMs: lagged.remainingMs };
  return current.phase === 'decide' && lagged.phase === 'decide' ? OPEN : CLOSED;
};

/** Talep CANLI mi: acik ('new') ve teklif ya da karar fazinda. Yoklama yalniz bu surede. */
export const isLiveTask = (input: PhaseInput, nowMs: number): boolean => {
  if (input.taskStatusCode !== 'new') return false;
  const { phase } = phaseOf(input, nowMs);
  return phase === 'offer' || phase === 'decide';
};

/** Talep satirinin faz girdisi. Teklif satiri (`taskFromOffer`) da ayni alanlari tasiyor. */
export const phaseInputOf = (task: TaskRow, receivedAtMs: number): PhaseInput => ({
  taskStatusCode: task.taskStatusCode,
  countdownStatus: task.countdownStatus,
  statusUpdatedAt: task.statusUpdatedAt,
  countdown: task.countdown,
  receivedAtMs,
});

/* -------------------------------------------------------------------------- */
/* Siralama                                                                    */
/* -------------------------------------------------------------------------- */

export type BestFirstSort = 'offerPrice:asc' | 'offerPrice:desc';

/**
 * "EN IYI" YONE BAGLI — sunucunun `bestOfferPrice` kuraliyla AYNI (readTaskScript ozeti).
 * `offer_price` her iki yonde de "1 birim = X ikinci para birimi":
 *   musteri ALIYOR (taskIsBuying) -> en DUSUK kur en iyisi (daha az oder)
 *   musteri SATIYOR               -> en YUKSEK kur en iyisi (daha cok alir)
 */
export const bestFirstSort = (taskIsBuying: boolean): BestFirstSort =>
  taskIsBuying ? 'offerPrice:asc' : 'offerPrice:desc';

/**
 * Listeye giden siralama: kullanici bir cip sectiyse o, secmediyse YONE GORE en iyi once.
 * Yon henuz bilinmiyorsa '' (sunucu varsayilani) — talep satiri gelmeden yanlis yone siralamak,
 * musteriye en KOTU teklifi en ustte gostermek olurdu.
 */
export const offerListSort = (userSort: string, taskIsBuying: boolean | undefined): string => {
  if (userSort !== '') return userSort;
  return taskIsBuying === undefined ? '' : bestFirstSort(taskIsBuying);
};

/* -------------------------------------------------------------------------- */
/* Kazanan sabitleme                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Karar verildiyse kazanan listenin USTUNE sabitlenir ve sayfadaki listeden cikarilir.
 *
 * Kazanan AYRI bir sorgudan gelir (`offerStatus=approved`): liste sayfalanmis ve fiyata gore
 * sirali, kazanan ikinci sayfada olabilir. O sorgu henuz donmediyse sayfadaki kabul edilmis
 * satir kullanilir; iki kaynak ayni teklifi IKI KEZ cizmez.
 */
export const splitWinner = (
  items: readonly OfferRow[],
  pinned: OfferRow | undefined,
  decided: boolean,
): { winner: OfferRow | null; others: OfferRow[] } => {
  if (!decided) return { winner: null, others: [...items] };
  const winner = pinned ?? items.find((offer) => offer.isAccepted) ?? null;
  return {
    winner,
    others: items.filter((offer) => !offer.isAccepted && offer.id !== winner?.id),
  };
};

/* -------------------------------------------------------------------------- */
/* Ust kart ve geri baglantisi                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Talep satiri alinamadiysa (hata) ust kart ilk teklif satirindan kurulur — sayfa eskiden
 * TAMAMEN teklif satirlarindan besleniyordu, o dayaniklilik korunuyor. Teklif satirinin
 * `createdAt`'i TEKLIFIN tarihi; talebin tarihi diye gosterilmez (null).
 */
export const taskFromOffer = (offer: OfferRow): TaskRow => ({
  id: offer.taskId,
  taskTypeCode: offer.taskTypeCode,
  taskIsBuying: offer.taskIsBuying,
  taskAmount: offer.taskAmount,
  taskStatusCode: offer.taskStatusCode,
  taskUserId: offer.taskUserId,
  pretenderUserId: offer.pretenderUserId,
  createdAt: null,
  statusUpdatedAt: offer.statusUpdatedAt,
  countdownStatus: offer.countdownStatus,
  countdown: offer.countdown,
  countdownDuration: offer.countdownDuration,
  userFullName: offer.taskUserFullName,
  userBusinessName: offer.taskUserBusinessName,
  taskData: offer.taskData,
  offerPrice: null,
  offerUserId: null,
  offerByUserId: null,
  offerByName: null,
  offerCount: null,
  bestOfferPrice: null,
  acceptedOfferPrice: null,
  acceptedBusinessName: null,
  marketOutcome: null,
});

/**
 * "Taleplerime don" hangi sekmeye? Taleplerim sekme eslemesinin (tasks/tab-params.ts) AYNASI:
 * kabul edilmis ve kapanmamis -> Islemde, teklif/karar fazindaki acik talep -> Acik Talepler,
 * gerisi -> Gecmis. Eskiden yalniz "karar var mi"ya bakiyordu ve tamamlanmis bir isi Islemde'ye
 * gonderiyordu.
 */
export const requestsTabFor = (
  task: Pick<TaskRow, 'taskStatusCode'> | null,
  phase: CountdownPhase,
): CustomerRequestTab => {
  if (task === null) return 'openRequests';
  if (isActiveAcceptedStatus(task.taskStatusCode)) return 'approvedRequests';
  if (task.taskStatusCode === 'new' && (phase === 'offer' || phase === 'decide')) {
    return 'openRequests';
  }
  return 'passedRequests';
};
